// Inhalt einer Tabellenzelle je Spalte (Smart-View-Spalten wie Close: display_name, primary_contact_name,
// primary_phone, primary_email, status_id/status_label, date_*, Custom Fields „custom.cf_…“ oder „cf_…“).
// Telefon und E-Mail stehen in den Listen nur als Symbole (Spalte „kontaktwege“, siehe listenSpalten.ts):
// Klick aufs Telefon ruft an, Klick auf den Brief öffnet eine neue E-Mail; beim Überfahren zeigt eine kleine Karte
// Nummer bzw. Adresse mit Kopierknopf.
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { FeldWert, LeadZeile } from "../api";
import type { MetaWert } from "../daten/meta";
import { emailVerfassen } from "../daten/schreiben";
import { useTelefonieKurz } from "../telefonie";
import { scoreStufe } from "../werkzeuge/statusFarben";
import { feldText, istLeer } from "../werkzeuge/felder";
import { berlinDatum, datumText, gueltig, zeitpunktLang } from "../werkzeuge/zeit";
import { KopierKnopf, ScoreAbzeichen, StatusPille } from "./Bausteine";
import { Icon } from "./Icon";
import { KONTAKTWEGE } from "./listenSpalten";
import { Telefonnummer, telefonText, useAnrufen } from "./Telefonnummer";

export function wertDerSpalte(z: LeadZeile, key: string): FeldWert | undefined {
  if (z.values && key in z.values) return z.values[key];
  const roh = key.replace(/^custom\./, "");
  if (roh.startsWith("cf_")) return z.custom?.[roh];
  switch (key) {
    case "display_name":
    case "name":
      return z.display_name ?? z.name;
    case "primary_contact_name":
      return z.primary_contact?.name ?? null;
    case "primary_phone":
      return z.primary_contact?.phone ?? null;
    case "primary_email":
      return z.primary_contact?.email ?? null;
    case "status_id":
      return z.status_id;
    case "status_label":
      return z.status_label;
    case "date_created":
      return z.date_created;
    case "date_updated":
      return z.date_updated;
    default:
      return (z as unknown as Record<string, FeldWert>)[key];
  }
}

/**
 * `berechnet` (Runde 5, Agent 5B): liefert für Spalten, die die Zeile nicht selbst trägt (Anzahl Anrufe, Ort …), den fertigen Inhalt;
 * `undefined` heißt „kenne ich nicht“, dann gilt die Darstellung unten.
 */
export function leadZelle(
  z: LeadZeile,
  key: string,
  meta: MetaWert,
  oeffnen: (e: MouseEvent<HTMLAnchorElement>) => void,
  berechnet?: (key: string) => ReactNode | undefined,
): ReactNode {
  if (key === KONTAKTWEGE) return <KontaktWege z={z} />;
  const extra = berechnet?.(key);
  if (extra !== undefined) return extra;
  const w = wertDerSpalte(z, key);
  const roh = key.replace(/^custom\./, "");
  if (key === "display_name" || key === "name") {
    return (
      <a href={`/lead/${encodeURIComponent(z.id)}`} className="tabelle__name" onClick={oeffnen} title={String(w ?? "")}>
        {String(w ?? z.name ?? "Ohne Namen")}
      </a>
    );
  }
  if (key === "status_id" || key === "status_label") {
    const s = key === "status_id" ? meta.status(String(w ?? "")) : meta.meta?.lead_statuses.find((x) => x.label === w);
    const label = s?.label ?? (key === "status_label" ? String(w ?? "") : z.status_label);
    return <StatusPille label={label} farbe={s?.color ?? meta.status(z.status_id)?.color} klein schlicht />;
  }
  if (istLeer(w)) {
    return <span className="leer-wert">—</span>;
  }
  if (key === "primary_phone") {
    return <Telefonnummer nummer={String(w)} leadId={z.id} contactId={z.primary_contact?.id} leadName={z.display_name ?? z.name} kontaktName={z.primary_contact?.name} kompakt />;
  }
  if (key === "primary_email") {
    return (
      <span className="zelle-mail">
        <a
          href={`mailto:${String(w)}`}
          title={`E-Mail schreiben: ${String(w)}`}
          onClick={(e) => {
            e.stopPropagation();
            if (emailVerfassen({ leadId: z.id, contactId: z.primary_contact?.id ?? null, an: String(w), leadName: z.display_name ?? z.name })) e.preventDefault();
          }}
        >
          {String(w)}
        </a>
        <KopierKnopf text={String(w)} label="E-Mail kopieren" />
      </span>
    );
  }
  if (key === "primary_contact_name") {
    const mehr = z.contacts_count > 1 ? ` +${z.contacts_count - 1}` : "";
    return (
      <span title={String(w)}>
        {String(w)}
        {mehr ? <span className="zelle-mehr">{mehr}</span> : null}
      </span>
    );
  }
  if (key === "date_created" || key === "date_updated") {
    const t = String(w);
    return gueltig(t) ? <span title={zeitpunktLang(t)}>{datumText(berlinDatum(t))}</span> : t;
  }
  if (roh.startsWith("cf_")) {
    const def = meta.feld(roh);
    if (def && /score/i.test(def.name)) {
      const abz = <ScoreAbzeichen wert={w} klein />;
      if (scoreStufe(w)) return abz;
    }
    const text = feldText(def, w, {
      nutzerName: (id) => meta.nutzerName(id),
      kontaktName: (id) => (z.primary_contact?.id === id ? z.primary_contact.name ?? "Kontakt" : "Kontakt"),
    });
    return <span title={text}>{text}</span>;
  }
  const text = Array.isArray(w) ? w.join(", ") : String(w);
  return <span title={text}>{text}</span>;
}

/** Neue E-Mail aus der Liste: der App-Rahmen nimmt das Ereignis ab und öffnet den CRM-Komposer (daten/schreiben.ts);
 * nur ohne Abnehmer öffnet sich das Mailprogramm über mailto. */
export { EMAIL_VERFASSEN, type EmailVerfassenDetail } from "../daten/schreiben";

function textOderNull(w: FeldWert | undefined): string | null {
  if (istLeer(w)) return null;
  const t = Array.isArray(w) ? String(w[0] ?? "") : String(w);
  return t.trim() || null;
}

type Karte = "telefon" | "mail" | null;

/** Telefon- und Brief-Symbol des Hauptkontakts, mit Schwebekarte (Nummer/Adresse und Kopieren). */
export function KontaktWege({ z }: { z: LeadZeile }) {
  const tel = useTelefonieKurz();
  const anrufen = useAnrufen();
  // Hauptkontakt zuerst; hat die View nur die Close-Spalten „phones“/„emails“, die erste davon
  const nummer = textOderNull(wertDerSpalte(z, "primary_phone")) ?? textOderNull(z.values?.phones);
  const mail = textOderNull(wertDerSpalte(z, "primary_email")) ?? textOderNull(z.values?.emails);
  const leadName = z.display_name ?? z.name;
  const kontaktId = z.primary_contact?.id ?? null;
  const [karte, setKarte] = useState<Karte>(null);
  const [anker, setAnker] = useState<HTMLElement | null>(null);
  const zeit = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(zeit.current), []);

  const zeigen = (art: Exclude<Karte, null>, el: HTMLElement, sofort = false) => {
    window.clearTimeout(zeit.current);
    const auf = () => {
      setAnker(el);
      setKarte(art);
    };
    if (sofort || karte) auf();
    else zeit.current = window.setTimeout(auf, 350);
  };
  const verbergen = () => {
    window.clearTimeout(zeit.current);
    zeit.current = window.setTimeout(() => setKarte(null), 180);
  };
  const halten = () => window.clearTimeout(zeit.current);

  const bereit = tel.status !== "nicht_eingerichtet";
  const nummerText = nummer ? telefonText(nummer) : null;
  const telTitel = nummerText ? (bereit ? `Anrufen: ${nummerText}` : `Nummer kopieren: ${nummerText}`) : "Keine Telefonnummer";
  const mailTitel = mail ? `E-Mail schreiben: ${mail}` : "Keine E-Mail-Adresse";

  return (
    <span className="kontaktwege">
      <button
        type="button"
        className="kontaktweg"
        disabled={!nummer}
        aria-label={telTitel}
        onMouseEnter={(e) => nummer && zeigen("telefon", e.currentTarget)}
        onMouseLeave={verbergen}
        onFocus={(e) => nummer && e.currentTarget.matches(":focus-visible") && zeigen("telefon", e.currentTarget, true)}
        onBlur={verbergen}
        onClick={(e) => {
          e.stopPropagation();
          if (!nummer) return;
          setKarte(null);
          void anrufen(nummer, z.id, kontaktId, { leadName, kontaktName: z.primary_contact?.name ?? null });
        }}
      >
        <Icon name="telefon" groesse={14} />
      </button>
      {mail ? (
        <a
          className="kontaktweg"
          href={`mailto:${mail}`}
          aria-label={mailTitel}
          onMouseEnter={(e) => zeigen("mail", e.currentTarget)}
          onMouseLeave={verbergen}
          onFocus={(e) => e.currentTarget.matches(":focus-visible") && zeigen("mail", e.currentTarget, true)}
          onBlur={verbergen}
          onClick={(e) => {
            e.stopPropagation();
            setKarte(null);
            if (emailVerfassen({ leadId: z.id, contactId: kontaktId, an: mail, leadName })) e.preventDefault();
          }}
        >
          <Icon name="mail" groesse={14} />
        </a>
      ) : (
        <button type="button" className="kontaktweg" disabled aria-label={mailTitel} title={mailTitel}>
          <Icon name="mail" groesse={14} />
        </button>
      )}
      {karte && anker ? (
        <Schwebekarte anker={anker} onBetreten={halten} onVerlassen={verbergen}>
          {karte === "telefon" && nummer ? (
            <>
              <span className="schwebekarte__wert">{nummerText}</span>
              <KopierKnopf text={nummer} label="Nummer kopieren" />
              <span className="schwebekarte__hinweis">{bereit ? "Klick aufs Symbol ruft an" : `${tel.grund ?? "Telefonie nicht bereit."} Klick kopiert die Nummer.`}</span>
            </>
          ) : karte === "mail" && mail ? (
            <>
              <span className="schwebekarte__wert">{mail}</span>
              <KopierKnopf text={mail} label="E-Mail kopieren" />
              <span className="schwebekarte__hinweis">Klick aufs Symbol öffnet eine neue E-Mail</span>
            </>
          ) : null}
        </Schwebekarte>
      ) : null}
    </span>
  );
}

/** Kleine Karte unter einem Symbol (Portal, feste Position, bleibt im Fenster). Schließt beim Scrollen. */
function Schwebekarte({ anker, children, onBetreten, onVerlassen }: { anker: HTMLElement; children: ReactNode; onBetreten: () => void; onVerlassen: () => void }) {
  const karte = useRef<HTMLDivElement>(null);
  const [lage, setLage] = useState<{ left: number; top: number } | null>(null);
  useEffect(() => {
    const r = anker.getBoundingClientRect();
    const breite = karte.current?.offsetWidth ?? 240;
    const hoehe = karte.current?.offsetHeight ?? 60;
    const left = Math.max(8, Math.min(r.left - 8, window.innerWidth - breite - 8));
    const unten = r.bottom + 6 + hoehe <= window.innerHeight - 8;
    setLage({ left, top: unten ? r.bottom + 6 : r.top - hoehe - 6 });
    const weg = () => onVerlassen();
    window.addEventListener("scroll", weg, true);
    return () => window.removeEventListener("scroll", weg, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anker]);
  return createPortal(
    <div
      ref={karte}
      className="schwebekarte"
      role="tooltip"
      style={{ left: lage?.left ?? -10000, top: lage?.top ?? 0, opacity: lage ? 1 : 0 }}
      onMouseEnter={onBetreten}
      onMouseLeave={onVerlassen}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}
